/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/pages/partners/Suppliers.tsx
 * ----------------------------------------------------------------------------
 * Lista de Fornecedores com:
 *  - Botão "Novo fornecedor" (rota /partners/suppliers/new)
 *  - Ação "Editar" por linha   (rota /partners/suppliers/:id/edit)
 *  - **Ação "Ocorrências"** por linha → abre a NOVA página de acompanhamento
 *    filtrada por fornecedor: /partners/supplier-issues?supplier=<id>&supplierName=<nome>
 *
 * Importante:
 * - Removemos o modal antigo de “Nova ocorrência”. A criação das ocorrências
 *   agora acontece no fluxo de **Recebimento**. Nesta lista apenas
 *   navegamos para a tela de acompanhamento das ocorrências.
 * - O endpoint usado pelo CrudList segue /api/partners/suppliers/.
 * - Coluna EDI é tolerante a schema: exibe row.edi || row.edi_code.
 * ========================================================================== */

import { useNavigate, Link } from 'react-router-dom'
import CrudList from '@/components/CrudList'
import { Column } from '@/components/DataTable'

type Supplier = {
  id: number
  code: string
  name: string
  // tolerantes (dependem do backend):
  edi?: string | null
  edi_code?: string | null
  email?: string | null
  priority?: number | null
  note?: string | null
  is_active?: boolean | null
}

export default function Suppliers() {
  const navigate = useNavigate()

  // Colunas da grade
  const columns: Column<Supplier>[] = [
    { key: 'code', header: 'Código', className: 'whitespace-nowrap' },
    { key: 'name', header: 'Nome', className: 'min-w-[220px]' },
    {
      key: 'edi',
      header: 'EDI',
      className: 'whitespace-nowrap',
      // Compatível com backends que retornam 'edi' ou 'edi_code'
      render: (row) => row.edi ?? (row as any).edi_code ?? '',
    },
    {
      key: 'priority',
      header: 'Prioridade',
      className: 'text-center',
      render: (r) => (r.priority ?? '') as any,
    },
    {
      key: 'is_active',
      header: 'Ativo',
      className: 'text-center',
      render: (r) => (r.is_active ? 'Sim' : 'Não'),
    },
    {
      key: 'id',
      header: 'Ações',
      className: 'text-right whitespace-nowrap',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          {/* Editar fornecedor */}
          <button
            className="btn sm subtle"
            onClick={() => navigate(`/partners/suppliers/${row.id}/edit`)}
            title="Editar fornecedor"
          >
            Editar
          </button>

          {/* NOVO fluxo: ir para a tela de ocorrências filtrada por fornecedor */}
          <Link
            className="btn sm"
            title="Ver ocorrências deste fornecedor"
            to={`/partners/supplier-issues?supplier=${row.id}&supplierName=${encodeURIComponent(
              row.name || '',
            )}`}
          >
            Ocorrências
          </Link>

          {/* ADR-0056 C1c-2: contratos de shelf-life deste fornecedor */}
          <Link
            className="btn sm subtle"
            title="Contratos de shelf-life deste fornecedor"
            to={`/partners/suppliers/${row.id}/shelf-life-contracts`}
          >
            Contratos
          </Link>
        </div>
      ),
    },
  ]

  return (
    <>
      {/* Toolbar simples acima da lista */}
      <div className="mb-2 flex justify-end">
        <button
          className="btn"
          onClick={() => navigate('/partners/suppliers/new')}
        >
          Novo fornecedor
        </button>
      </div>

      <CrudList
        title="Fornecedores"
        endpoint="/api/partners/suppliers/"
        columns={columns}
      />
    </>
  )
}
